import type { GlColumnDef } from '@/services/accountService/glInvoiceService';
import { columnDisplayName, resolveColumnZh } from '@/services/accountService/glInvoiceService';

export const SHOW_CLIENT_ALIAS_STORAGE_KEY = 'gl.showClientAlias';

/** 列是否带有与标准名不同的客户别名 */
export function hasDistinctClientAlias(
    def: Pick<GlColumnDef, 'nameEn' | 'nameZh' | 'aliasEn' | 'aliasZh'>,
    fallbackZh?: string,
): boolean {
    const item = columnDisplayName(def, fallbackZh);
    const client = clientAliasDisplayName(def);
    if (!client) return false;
    return client.toLowerCase() !== item.toLowerCase();
}

/** 客户别名展示串（EN + ZH）；无别名时返回空 */
export function clientAliasDisplayName(
    def: Pick<GlColumnDef, 'aliasEn' | 'aliasZh'>,
): string {
    const en = (def.aliasEn || '').trim();
    const zh = (def.aliasZh || '').trim();
    if (zh && en && zh !== en) return `${en} ${zh}`.trim();
    return en || zh;
}

export type ColumnLabelParts = {
    primary: string;
    /** 客户别名；无或与标准相同则为 null */
    client: string | null;
    hasAlias: boolean;
};

export function resolveColumnLabelParts(
    def: GlColumnDef,
    fallbackZh?: string,
): ColumnLabelParts {
    const primary = columnDisplayName(def, fallbackZh);
    const clientRaw = clientAliasDisplayName(def);
    const hasAlias = !!clientRaw && clientRaw.toLowerCase() !== primary.toLowerCase();
    return {
        primary,
        client: hasAlias ? clientRaw : null,
        hasAlias,
    };
}

/**
 * 表头 / Tiny tab 展示名：默认标准名；开启 showAlias 时追加客户名。
 */
export function columnHeaderLabelWithAlias(
    def: Pick<GlColumnDef, 'nameEn' | 'nameZh' | 'aliasEn' | 'aliasZh' | 'glCode'>,
    showAlias: boolean,
    fallbackZh?: string,
): string {
    const parts = resolveColumnLabelParts(def as GlColumnDef, fallbackZh);
    // 开：客户名；关：标准科目名（Sales income vs Sales of goods）
    if (showAlias && parts.client) return parts.client;
    return parts.primary;
}

/**
 * 下拉/搜索标签：默认标准名；开启 showAlias 时追加客户名。
 * 始终把 alias 放进括号外可搜字段：无 showAlias 时用不可见策略——把 alias 附在末尾用 · 连接方便搜。
 */
export function columnOptionLabelWithAlias(
    def: GlColumnDef,
    fallbackZh: string | undefined,
    showAlias: boolean,
): string {
    const parts = resolveColumnLabelParts(def, fallbackZh);
    const base = def.glCode ? `${parts.primary} (${def.glCode})` : parts.primary;
    if (!parts.client) return base;
    if (showAlias) return `${parts.primary} · ${parts.client}${def.glCode ? ` (${def.glCode})` : ''}`;
    // 隐藏展示时仍把 alias 附上，便于按客户名搜索（UI 读 primary；搜索用整串）
    return `${base} · ${parts.client}`;
}

export function readShowClientAliasPreference(): boolean {
    try {
        return localStorage.getItem(SHOW_CLIENT_ALIAS_STORAGE_KEY) === '1';
    } catch {
        return false;
    }
}

export function writeShowClientAliasPreference(on: boolean) {
    try {
        localStorage.setItem(SHOW_CLIENT_ALIAS_STORAGE_KEY, on ? '1' : '0');
    } catch {
        // ignore
    }
}

/** 标准中文（忽略 aliasZh） */
export function standardColumnZh(
    def: Pick<GlColumnDef, 'nameZh'>,
    fallbackZh?: string,
): string {
    return resolveColumnZh({ nameZh: def.nameZh }, fallbackZh);
}
